// Supprimer ses propres créations (D-178, Idries : « Il faut aussi permettre de supprimer ses propres drills, exos et jeux
// pour le coach. ») : le bouton « Supprimer » d'un drill, d'un exercice, d'un jeu ou d'un paquet de cartes, montré à son
// auteur et au bureau seulement (la base refuse les autres). Comme les séances types (plan.ts) : deux gestes. Au premier,
// « Confirmer : supprimer » et, dessous, où c'est utilisé en une ligne ; au second, supprimé (archived_at, rien ne
// s'efface) et un message avec « Annuler » pendant 6 s. Les séances gardent l'élément tel quel.
import { archiveExercise, libraryUsage } from '../lib/api';
import { archiveDrill } from '../lib/drills-api';
import { archiveDeck, archiveGame } from '../lib/games-api';
import { LIB_NOUN, readUsage, usageLine, type LibKind } from '../domain/library';
import { confirmTwice, html, toast, toastUndo } from './dom';

const ARCHIVE: Record<LibKind, (id: string, on: boolean) => Promise<void>> = {
  drill: archiveDrill, exercise: archiveExercise, game: archiveGame, deck: archiveDeck,
};

/** Le bouton (à câbler avec `wireDelete`) : « Supprimer », discret, à son auteur et au bureau seulement. */
export const deleteBtn = (kind: LibKind, id: string, label = 'Supprimer') => html`<button type="button" class="btn btn-link btn-sm sup-del" data-sup="${kind}:${id}">${label}</button>`;

export interface DeleteOpts {
  /** Après la suppression (et après « Annuler ») : recharger la liste, revenir en arrière. */
  after: () => void;
  /** Où écrire la ligne « où c'est utilisé » (par défaut : juste après le bouton). */
  lineHost?: HTMLElement | null;
}

/** Câble un bouton « Supprimer » (`deleteBtn`) : deux gestes, la ligne d'usage au premier, « Annuler » après. */
export function wireDelete(btn: HTMLButtonElement, opts: DeleteOpts): void {
  const [kind, id] = (btn.dataset.sup ?? '').split(':') as [LibKind, string];
  if (!ARCHIVE[kind] || !id) return;
  const showUsage = () => {
    const line = document.createElement('span');
    line.className = 'sup-use';
    line.setAttribute('role', 'status');
    if (opts.lineHost) opts.lineHost.append(line); else btn.after(line);
    if (kind === 'deck') { line.textContent = usageLine('deck', readUsage({})); return; }
    line.textContent = '…';
    libraryUsage(kind, id).then(u => { line.textContent = usageLine(kind, readUsage(u)); }).catch(() => line.remove());
  };
  confirmTwice(btn, 'Confirmer : supprimer', async () => {
    try { await ARCHIVE[kind](id, true); }
    catch { toast('Seul son auteur ou le bureau peut le supprimer.', 'ko'); return; }
    toastUndo(`${LIB_NOUN[kind]} supprimé`, () => {
      ARCHIVE[kind](id, false).then(() => { toast(`${LIB_NOUN[kind]} remis`); opts.after(); }, () => toast('Impossible de le remettre.', 'ko'));
    });
    opts.after();
  }, showUsage);
}

/** Câble tous les boutons « Supprimer » d'un écran. */
export function wireDeletes(root: ParentNode, after: () => void): void {
  for (const b of root.querySelectorAll<HTMLButtonElement>('[data-sup]')) wireDelete(b, { after });
}
